import { useMemo } from "react"
import type { PreOrderMessage } from "@stockline/shared/types/orders/pre-order-message"
import { PRE_ORDER_MESSAGE_TABLE } from "../../../lib/offline/resources/orders/pre-order-message/db"
import { useAISync } from "../../ai/providers/AISyncProvider"

export interface UsePreOrderMessagesFilter {
  /** Find junctions linking THIS pre-order → its WhatsApp messages. */
  preOrderId?: string
  /** Find junctions linking THIS WhatsApp message → its pre-orders. */
  messageId?: string
  /** Find junctions linking THIS WhatsApp chat → all participating junctions. */
  chatId?: string
  /**
   * If true, include soft-deleted rows. Defaults to false — UI consumers
   * should not see tombstones; SSE deletes drop them from the visible list.
   */
  includeDeleted?: boolean
}

/**
 * Reactive read of the pre_order_messages junction (WhatsApp).
 *
 * Both directions are indexed: pre-order → messages (recap), AND
 * message/chat → pre-orders (chat-page badges + linked pre-orders list).
 */
export function usePreOrderMessages(
  filter?: UsePreOrderMessagesFilter
): PreOrderMessage[] {
  const { reactive } = useAISync()
  const rows = reactive.useQuery<PreOrderMessage>(PRE_ORDER_MESSAGE_TABLE, {
    ...(filter?.preOrderId ? { preOrderId: filter.preOrderId } : {}),
    ...(filter?.messageId ? { messageId: filter.messageId } : {}),
    ...(filter?.chatId ? { chatId: filter.chatId } : {}),
    orderBy: "-createdAt",
  })
  // Memoize the filtered slice so the returned array reference stays stable
  // across renders when the underlying Dexie query is unchanged.
  const includeDeleted = filter?.includeDeleted === true
  return useMemo(
    () => (includeDeleted ? rows : rows.filter((r) => !r.deletedAt)),
    [rows, includeDeleted]
  )
}
